import { formatUnits, parseUnits } from "viem";

/**
 * Token-amount conversions, all anchored on viem's `parseUnits`/`formatUnits`.
 *
 * The rule this module exists to enforce: **human input becomes raw `bigint`
 * at the boundary and stays raw**. Parse form text the moment it arrives,
 * do every contract-bound calculation in `bigint`, and only convert back to a
 * `number`/`string` at the display edge (`toNumber`/`toText`). Never feed a
 * `toNumber` result back into a contract argument — that round-trip is what
 * introduces the float dust that trips on-chain exact-amount checks.
 */

/**
 * Parse a user-typed or computed amount into raw token units.
 *
 * Accepts a string straight from a form field (anything outside `[0-9.]` is
 * stripped, so signs and grouping commas are ignored) or a `number` (clamped
 * to `decimals` fraction digits first). Blank, non-finite, zero, or negative
 * inputs all collapse to `0n`. Over-precise fractions are truncated to
 * `decimals` so `parseUnits` never throws.
 */
export const toRaw = (value: string | number, decimals: number): bigint => {
  let s: string;
  if (typeof value === "number") {
    if (!Number.isFinite(value) || value <= 0) return 0n;
    s = value.toFixed(decimals);
  } else {
    s = value.replace(/[^0-9.]/g, "");
  }
  const [intPart = "", fracPart = ""] = s.split(".");
  if (intPart === "" && fracPart === "") return 0n;
  const normalized = `${intPart || "0"}.${fracPart.slice(0, decimals)}`;
  try {
    const raw = parseUnits(normalized, decimals);
    return raw > 0n ? raw : 0n;
  } catch {
    return 0n;
  }
};

/**
 * Raw → `number`, for display and float-only sizing math. Never round-trip
 * this back into a contract argument.
 */
export const toNumber = (raw: bigint, decimals: number): number =>
  Number(formatUnits(raw, decimals));

/**
 * Raw → fixed-dp display string, truncating (never rounding up — the finance
 * display rule of thumb). Assumes a non-negative `raw`.
 */
export const toText = (raw: bigint, decimals: number, dp: number): string => {
  const denom = 10n ** BigInt(decimals);
  const whole = raw / denom;
  if (dp <= 0) return whole.toString();
  const frac = raw % denom;
  const fracStr = frac.toString().padStart(decimals, "0").slice(0, dp);
  return `${whole.toString()}.${fracStr}`;
};

/** Scale a raw amount up by `bps` basis points: `raw × (10000 + bps) / 10000`. */
export const addBps = (raw: bigint, bps: bigint): bigint =>
  (raw * (10_000n + bps)) / 10_000n;

/**
 * Inverse of `addBps`: the largest amount that, once `addBps`-buffered, still
 * fits under `raw`. Used to size a repay against a balance that must cover the
 * buffered approval.
 */
export const removeBps = (raw: bigint, bps: bigint): bigint =>
  (raw * 10_000n) / (10_000n + bps);

/** `a × b / denom`, rounded down. */
export const mulDiv = (a: bigint, b: bigint, denom: bigint): bigint =>
  (a * b) / denom;
